import AddIcon from '@mui/icons-material/Add'
import { Box, IconButton } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'

interface RowInsertProps {
	disabled?: boolean
	onInsertRow: (rowIndex: number) => void
	/** If provided, renders with fixed positioning at this rect (for portal usage) */
	rect?: DOMRect | null,
	rowIndex: number
}

/**
 * JIRA-style row insertion component
 * Shows a blue line and "+" button when hovering between rows
 */
export const RowInsert = ({
	rowIndex,
	onInsertRow,
	disabled = false,
	rect,
}: RowInsertProps) => {
	const [isHovering, setIsHovering] = useState(false)
	const timeoutRef = useRef<NodeJS.Timeout | null>(null)

	// Clean up timeout on unmount
	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}
		}
	}, [])

	const handleMouseEnter = useCallback(() => {
		if (disabled) return
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
		}
		// 200ms delay to prevent accidental triggers while scrolling
		timeoutRef.current = setTimeout(() => {
			setIsHovering(true)
		}, 200)
	}, [disabled])

	const handleMouseLeave = useCallback(() => {
		// Clear the enter timeout if it hasn't fired yet
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
		}
		// Small delay to prevent flickering when moving between hover zone and button
		timeoutRef.current = setTimeout(() => {
			setIsHovering(false)
		}, 100)
	}, [])

	const handleInsert = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation()
			e.preventDefault()
			if (!disabled) {
				onInsertRow(rowIndex)
			}
		},
		[disabled, onInsertRow, rowIndex],
	)

	if (disabled) return null

	// Fixed positioning for portal usage
	const containerSx = rect
		? {
				position: 'fixed' as const,
				top: rect.bottom - 7,
				left: rect.left,
				width: rect.width,
				height: 14,
				zIndex: 100,
				cursor: 'pointer',
			}
		: {
				position: 'absolute' as const,
				bottom: -6,
				left: 0,
				right: 0,
				height: 12,
				zIndex: 100,
				cursor: 'pointer',
			}

	return (
		<Box
			sx={containerSx}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onClick={handleInsert}
		>
			{/* Blue line indicator - spans full width */}
			<Box
				sx={{
					position: 'absolute',
					top: '50%',
					left: 0,
					right: 0,
					height: 2,
					bgcolor: '#2684FF',
					opacity: isHovering ? 1 : 0,
					transition: 'opacity 0.1s ease',
					transform: 'translateY(-50%)',
					pointerEvents: 'none',
				}}
			/>

			{/* Plus button on the left side */}
			<Box
				sx={{
					position: 'absolute',
					top: '50%',
					left: 0,
					transform: 'translateY(-50%)',
					opacity: isHovering ? 1 : 0,
					transition: 'opacity 0.1s ease',
					zIndex: 1001,
				}}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				<IconButton
					size="small"
					onClick={handleInsert}
					sx={{
						bgcolor: '#2684FF',
						color: 'white',
						width: 20,
						height: 20,
						p: 0,
						'&:hover': {
							bgcolor: '#0065FF',
						},
						boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
					}}
				>
					<AddIcon sx={{ fontSize: 14 }} />
				</IconButton>
			</Box>
		</Box>
	)
}
